import * as moment from 'moment';
import {FilterTimePickerValue} from '../shared/components/filter-time-picker/filter-time-picker.component';
import {JourneyMode} from './group-sales-search.model';

export const PAST_DATE_HINT = 'Dates in the past can\'t be searched';
export const TIME_ORDER_HINT = 'From time must not be later than To time';
export const RETURN_START_ORDER_HINT = 'Return start date cannot be before the outbound start date';
export const RETURN_END_ORDER_HINT = 'Return end date cannot be before the return start date';

export interface GroupSalesFilterState {
    journeyMode: JourneyMode;
    startDate: Date;
    endDate: Date;
    time: FilterTimePickerValue;
    groupSize: number | null | undefined;
    returnStartDate?: Date;
    returnEndDate?: Date;
    returnTime?: FilterTimePickerValue;
}

/** Local midnight of today's UK calendar date. */
export function ukToday(): Date {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(new Date());
    const part = (type: string) => Number(parts.find(p => p.type === type).value);
    return new Date(part('year'), part('month') - 1, part('day'));
}

function startOfDay(d: Date): number {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function isPastDate(d: Date | null | undefined, today: Date = ukToday()): boolean {
    return !!d && startOfDay(d) < startOfDay(today);
}

function secondsOfDay(d: Date | string): number {
    const m = moment(d);
    return m.hours() * 3600 + m.minutes() * 60 + m.seconds();
}

export function isTimeWindowInOrder(start: Date | string | null, end: Date | string | null): boolean {
    if (!start || !end) {
        return true;
    }
    return secondsOfDay(start) <= secondsOfDay(end);
}

export function formatTime24(d: Date): string {
    return moment(d).format('HH:mm:ss');
}

export function validateGroupSalesFilters(
    s: GroupSalesFilterState, today: Date = ukToday()
): { valid: boolean; hint: string | null } {
    const isReturn = s.journeyMode === 'return';
    const dates = [s.startDate, s.endDate];
    if (isReturn) {
        dates.push(s.returnStartDate, s.returnEndDate);
    }
    const anyPast = dates.some(d => isPastDate(d, today));
    const timesInOrder = isTimeWindowInOrder(s.time?.startTime ?? null, s.time?.endTime ?? null)
        && (!isReturn || isTimeWindowInOrder(s.returnTime?.startTime ?? null, s.returnTime?.endTime ?? null));
    const groupSizeOk = typeof s.groupSize === 'number' && Number.isInteger(s.groupSize) && s.groupSize >= 1;

    let returnOk = true;
    let returnStartBeforeOutbound = false;
    let returnEndBeforeStart = false;
    if (isReturn) {
        returnStartBeforeOutbound = !!s.returnStartDate && !!s.startDate
            && startOfDay(s.returnStartDate) < startOfDay(s.startDate);
        returnEndBeforeStart = !!s.returnStartDate && !!s.returnEndDate
            && startOfDay(s.returnEndDate) < startOfDay(s.returnStartDate);
        returnOk = !!s.returnStartDate && !!s.returnEndDate
            && !!s.returnTime?.startTime && !!s.returnTime?.endTime
            && !returnEndBeforeStart && !returnStartBeforeOutbound;
    }

    let hint: string | null = null;
    if (anyPast) {
        hint = PAST_DATE_HINT;
    } else if (!timesInOrder) {
        hint = TIME_ORDER_HINT;
    } else if (returnStartBeforeOutbound) {
        hint = RETURN_START_ORDER_HINT;
    } else if (returnEndBeforeStart) {
        hint = RETURN_END_ORDER_HINT;
    }
    return {valid: !anyPast && timesInOrder && groupSizeOk && returnOk, hint};
}
